import { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import Shepherd, { Tour, TourOptions } from "shepherd.js";
import { useGetCurrentUser } from "~/api/users/users";
import { trackEvent } from "~/utils/analytics";
import {
  createCasePageSteps,
  createSkyViewSteps,
  TourStepId,
} from "../constants/tourSteps";

const STORAGE_KEY = "sky_onboarding_tour_complete";
const ELEMENT_WAIT_TIMEOUT = 3000;
const ELEMENT_POLL_INTERVAL = 500;

type TourType = "casePage" | "skyView";

interface UseOnboardingTourOptions {
  tourType: TourType;
  enabled?: boolean;
}

const tourOptions: TourOptions = {
  defaultStepOptions: {
    cancelIcon: {
      enabled: false,
    },
    classes: "shepherd-theme-custom",
    scrollTo: { behavior: "smooth", block: "center" },
  },
  useModalOverlay: true,
};

const waitForElement = (
  selector: string,
  timeout = ELEMENT_WAIT_TIMEOUT
): Promise<Element | null> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(interval);
        resolve(el);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        resolve(null);
      }
    }, ELEMENT_POLL_INTERVAL);
  });
};

export const useOnboardingTour = ({
  tourType,
  enabled = true,
}: UseOnboardingTourOptions) => {
  const intl = useIntl();
  const { data: user } = useGetCurrentUser();
  const tourRef = useRef<Tour | null>(null);
  const hasStartedRef = useRef(false);

  const isOnboardingComplete = useCallback(() => {
    return localStorage.getItem(STORAGE_KEY) === "true";
  }, []);

  const markOnboardingComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    trackEvent({
      eventName: "OnboardingTour_Completed",
      properties: { totalSteps: 3 },
    });
  }, []);

  const markOnboardingSkipped = useCallback((currentStep: TourStepId) => {
    localStorage.setItem(STORAGE_KEY, "true");
    trackEvent({
      eventName: "OnboardingTour_Skipped",
      properties: { step: currentStep },
    });
  }, []);

  const shouldShowTour = useCallback(() => {
    if (!user?.hasCompletedFtue) return false;
    if (isOnboardingComplete()) return false;
    return true;
  }, [user, isOnboardingComplete]);

  const handleSkip = useCallback(
    (stepId: TourStepId) => {
      markOnboardingSkipped(stepId);
      if (tourRef.current) {
        tourRef.current.complete();
      }
    },
    [markOnboardingSkipped]
  );

  const handleComplete = useCallback(() => {
    markOnboardingComplete();
    if (tourRef.current) {
      tourRef.current.complete();
    }
  }, [markOnboardingComplete]);

  const startTour = useCallback(async () => {
    if (!enabled || !shouldShowTour() || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    const targetSelector =
      tourType === "casePage"
        ? '[data-tour="launch-sky"]'
        : '[data-tour="doc-list"]';

    const element = await waitForElement(targetSelector);
    if (!element) {
      hasStartedRef.current = false;
      return;
    }

    const steps =
      tourType === "casePage"
        ? createCasePageSteps({
            intl,
            onSkip: handleSkip,
            onComplete: handleComplete,
          })
        : createSkyViewSteps({
            intl,
            onSkip: handleSkip,
            onComplete: handleComplete,
          });

    const tour = new Shepherd.Tour(tourOptions) as Tour;

    steps.forEach((step) => {
      tour.addStep(step);
    });

    tour.on("show", (event: { step: { id: string } }) => {
      trackEvent({
        eventName: "OnboardingTour_StepViewed",
        properties: {
          step: event.step.id,
          tourType,
        },
      });
    });

    tour.on("start", () => {
      trackEvent({
        eventName: "OnboardingTour_Started",
        properties: {
          step: steps[0]?.id || "unknown",
          tourType,
        },
      });
    });

    tourRef.current = tour;
    tour.start();
  }, [enabled, shouldShowTour, tourType, intl, handleSkip, handleComplete]);

  const cancelTour = useCallback(() => {
    if (tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }
    hasStartedRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      cancelTour();
    };
  }, [cancelTour]);

  return {
    startTour,
    cancelTour,
    isOnboardingComplete,
    shouldShowTour,
  };
};
